import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { makeStyles, Spinner, shorthands } from '@fluentui/react-components';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  mainViewContainer: {
    width: '100%',
    backgroundColor: 'rgb(0,0,0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
  },
  mainViewContentContainer: {
    height: '41px',
    width: '80px',
    backgroundColor: 'rgb(50,49,48, 0.6)',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  mainViewIconLabelContainer: {
    marginLeft: '6px',
  },
  contentIcon: {
    width: '25px',
  },

  footerContainer: {
    backgroundColor: '#2F2F2F',
    display: 'flex',
    height: '48px',
    ...shorthands.padding('10px'),
    alignItems: 'center',
    lineHeight: '21px',
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
  },
  footerInvalid: {
    backgroundColor: '#FFF4CE',
  }, // required otherwise nested classnames will not work
  footerContent: {
    // color: palette.white,
    display: 'flex',
    marginLeft: '8px',
    alignItems: 'center',
  },
  helpLink: {
    color: '#337ab7',
    ...shorthands.textDecoration('underline'),
    fontWeight: 'normal',
    backgroundColor: 'transparent',
    ...shorthands.borderRadius('0px', '0px', '0px', '0px'),
    ...shorthands.border('none'),
  },
});

export const ItemPlaceholderBoxViewFooterType = {
  info: 'info',
  warning: 'warning',
};

export const ItemPlaceholderBoxView = ({
  className,
  mainViewIcon,
  mainViewIconLabel,
  footerProps,
}) => {
  const {
    type,
    summary,
    tip,
    learnMoreLinkText,
    learnMoreLinkTopic,
  } = footerProps || {};

  const classes = useStyles();

  const renderFooter = useCallback(() => {
    let content;

    if (footerProps) {
      content = (
        <div className={classnames(
            classes.footerContainer,
            type === ItemPlaceholderBoxViewFooterType.warning && classes.footerInvalid
          )}
        >
          <Spinner size="small" />
          <div className={classes.footerContent}>
            {
              summary &&
              <span className={classes.footContentStatus}><b className="summary">{summary}</b></span>
            }
            {
              tip &&
              <React.Fragment>
                <span>&nbsp;</span>
                <span className="tip">{tip}</span>
              </React.Fragment>
            }
            {
              learnMoreLinkText && learnMoreLinkTopic &&
              <React.Fragment>
                <span>&nbsp;</span>
                <button className={classnames(`HelpTopic ${classes.helpLink}`)} query={`help:${learnMoreLinkTopic}`}>{learnMoreLinkText}</button>
              </React.Fragment>
            }
          </div>
        </div>
      );
    }

    return content;
  }, [classes.footContentStatus, classes.footerContainer, classes.footerContent, classes.footerInvalid, classes.helpLink, footerProps, learnMoreLinkText, learnMoreLinkTopic, summary, tip, type]);

  return (
    <div className={classnames(classes.container, className)}>
      <div className={classes.mainViewContainer}>
        <div className={classes.mainViewContentContainer}>
          <img src={mainViewIcon} className={classes.contentIcon} alt="" />
          <span className={classes.mainViewIconLabelContainer}>{mainViewIconLabel}</span>
        </div>
      </div>
      {renderFooter()}
    </div>
  );
};

ItemPlaceholderBoxView.propTypes = {
  className: PropTypes.string,
  mainViewIcon: PropTypes.string.isRequired,
  mainViewIconLabel: PropTypes.string.isRequired,
  footerProps: PropTypes.shape({
    type: PropTypes.oneOf(Object.keys(ItemPlaceholderBoxViewFooterType)),
    renderIcon: PropTypes.func,
    summary: PropTypes.string,
    tip: PropTypes.string,
    learnMoreLinkText: PropTypes.string,
    learnMoreLinkTopic: PropTypes.string,
  }),
};

ItemPlaceholderBoxView.defaultProps = {
  className: '',
  footerProps: {},
};
